import { useEffect, useMemo, useState } from 'react'

import { api } from '../api/client'
import type {
  CatalogoRuoli,
  LivelloPermesso,
  RuoloPersonalizzato,
  RuoloPersonalizzatoIn,
} from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner } from './common'

/**
 * Ruoli personalizzati: un ruolo di partenza più un livello per ogni voce del
 * menu — nascosto, disattivato, lettura, scrittura.
 *
 * Tre colonne: i ruoli del tenant, la matrice dei permessi (il menu, gruppo
 * per gruppo, con un comando per il gruppo intero) e l'**anteprima del menu**
 * così come lo vedrà chi ha quel ruolo. L'anteprima non è decorazione: la
 * matrice dice «cosa ho deciso», l'anteprima «cosa succederà», e un errore
 * (una sezione intera sparita) si vede lì prima di salvarlo.
 *
 * Il ruolo può solo **restringere** quello di partenza: con «Sola lettura»
 * come partenza la scrittura non si offre.
 *
 * The predefined roles of CENSO come first: they are shown like the others but
 * read-only (their permissions follow the code), and «Duplica» makes a copy to
 * change. Il controllo vero è nel Control
 * Plane (app/services/permessi_service.py); vedi
 * docs/architecture/ruoli-personalizzati.md.
 */

const LIVELLI: LivelloPermesso[] = ['hidden', 'disabled', 'read', 'write']

function etichettaLivello(l: LivelloPermesso): string {
  return {
    hidden: t('Nascosto'),
    disabled: t('Disattivato'),
    read: t('Lettura'),
    write: t('Scrittura'),
  }[l]
}

function spiegazioneLivello(l: LivelloPermesso): string {
  return {
    hidden: t('La voce non compare nel menu e le sue API sono chiuse.'),
    disabled: t('La voce si vede in grigio con il lucchetto, ma non si apre.'),
    read: t('Si consulta tutto, senza poter modificare.'),
    write: t('Tutto quello che il ruolo di partenza consente.'),
  }[l]
}

const ICONA: Record<LivelloPermesso, string> = { hidden: '⊘', disabled: '🔒', read: '👁', write: '✎' }

function ruoliDiPartenza() {
  return [
    { value: 'tenant_admin', label: t('Amministratore'), hint: t('Gestisce utenti, credenziali e sonde') },
    { value: 'tenant_operator', label: t('Operatore'), hint: t('Avvia discovery e consulta i dati') },
    { value: 'read_only', label: t('Sola lettura'), hint: t('Può solo consultare') },
  ]
}

const COLORI = ['#c8102e', '#e8590c', '#f59f00', '#2f9e44', '#0c8599', '#1971c2', '#6741d9', '#495057']

/** The name to show: a predefined role's is translated, a custom one is the tenant's own. */
export function nomeDelRuolo(r: { name: string; preset?: string | null }): string {
  return r.preset ? t(r.name) : r.name
}

function vuoto(): RuoloPersonalizzatoIn {
  return { name: '', description: '', color: COLORI[5], base_role: 'tenant_operator', permissions: { '*': 'read' } }
}

function livello(bozza: RuoloPersonalizzatoIn, chiave: string): LivelloPermesso {
  return bozza.permissions[chiave] ?? bozza.permissions['*'] ?? 'read'
}

/** Con «Sola lettura» come partenza la scrittura non esiste: si mostra lettura. */
function efficace(bozza: RuoloPersonalizzatoIn, l: LivelloPermesso): LivelloPermesso {
  return bozza.base_role === 'read_only' && l === 'write' ? 'read' : l
}

export function RuoliPersonalizzati({ onChanged }: { onChanged: () => void }) {
  const catalogo = useAsync(() => api.roleCatalog(), [])
  const ruoli = useAsync(() => api.customRoles(), [])
  const [scelto, setScelto] = useState<string | 'nuovo' | null>(null)
  const [bozza, setBozza] = useState<RuoloPersonalizzatoIn>(vuoto)
  const [chiusi, setChiusi] = useState<string[]>([])
  const [errore, setErrore] = useState<unknown>(null)
  const [salvo, setSalvo] = useState(false)
  const [salvato, setSalvato] = useState(false)

  const corrente = ruoli.data?.find((r) => r.id === scelto)
  // A predefined role is looked at, not edited.
  const bloccato = !!corrente?.preset
  const predefiniti = (ruoli.data ?? []).filter((r) => r.preset)
  const propri = (ruoli.data ?? []).filter((r) => !r.preset)

  // Si apre il primo ruolo, se ce n'è uno: la pagina parte da qualcosa da
  // guardare invece che da un modulo vuoto.
  useEffect(() => {
    if (scelto === null && ruoli.data && ruoli.data.length > 0) apri(ruoli.data[0])
  }, [ruoli.data]) // eslint-disable-line react-hooks/exhaustive-deps

  function apri(r: RuoloPersonalizzato) {
    setScelto(r.id)
    setBozza({
      name: nomeDelRuolo(r),
      description: r.preset ? t(r.description) : r.description,
      color: r.color,
      base_role: r.base_role,
      permissions: { ...r.permissions },
    })
    setErrore(null)
    setSalvato(false)
  }

  function nuovo() {
    setScelto('nuovo')
    setBozza(vuoto())
    setErrore(null)
    setSalvato(false)
  }

  function duplica() {
    setScelto('nuovo')
    setBozza((b) => ({ ...b, name: t('{nome} (copia)', { nome: b.name }) }))
    setSalvato(false)
  }

  function imposta(chiavi: string[], l: LivelloPermesso) {
    setBozza((b) => {
      const permessi = { ...b.permissions }
      for (const k of chiavi) permessi[k] = l
      return { ...b, permissions: permessi }
    })
    setSalvato(false)
  }

  function cambiaPartenza(base: string) {
    setBozza((b) => {
      if (base !== 'read_only') return { ...b, base_role: base }
      // Scendendo a «Sola lettura» le scritture diventano letture per davvero,
      // non solo in apparenza: salvare quello che si vede.
      const permessi = Object.fromEntries(
        Object.entries(b.permissions).map(([k, v]) => [k, v === 'write' ? 'read' : v]),
      ) as Record<string, LivelloPermesso>
      return { ...b, base_role: base, permissions: permessi }
    })
    setSalvato(false)
  }

  async function salva() {
    setErrore(null)
    setSalvo(true)
    try {
      const fatto =
        scelto && scelto !== 'nuovo'
          ? await api.updateCustomRole(scelto, bozza)
          : await api.createCustomRole(bozza)
      await ruoli.reload()
      setScelto(fatto.id)
      setBozza({ name: fatto.name, description: fatto.description, color: fatto.color, base_role: fatto.base_role, permissions: fatto.permissions })
      setSalvato(true)
      onChanged()
    } catch (err) {
      setErrore(err)
    } finally {
      setSalvo(false)
    }
  }

  async function elimina() {
    if (!corrente) return
    if (!window.confirm(t('Eliminare il ruolo «{nome}»?', { nome: corrente.name }))) return
    setErrore(null)
    try {
      await api.deleteCustomRole(corrente.id)
      setScelto(null)
      setBozza(vuoto())
      await ruoli.reload()
      onChanged()
    } catch (err) {
      setErrore(err)
    }
  }

  const tutteLeChiavi = useMemo(
    () => (catalogo.data?.groups ?? []).flatMap((g) => g.modules.map((m) => m.key)),
    [catalogo.data],
  )

  if (catalogo.loading && !catalogo.data) return <Spinner />
  if (!catalogo.data) return <ErrorBanner error={catalogo.error} />

  return (
    <Card title={t('Ruoli e permessi')}>
      <p className="field__hint ruoli__intro">
        {t('Un ruolo personalizzato deriva da un ruolo predefinito e definisce, per ciascuna voce di menu, il relativo livello di accesso. Le autorizzazioni possono essere esclusivamente limitate rispetto al ruolo predefinito e non possono essere estese.')}
      </p>
      <ErrorBanner error={errore ?? ruoli.error} />

      <div className="ruoli">
        {/* --- I ruoli del tenant --- */}
        <aside className="ruoli__elenco">
          <button type="button" className="ruoli__nuovo" onClick={() => nuovo()}>
            + {t('Nuovo ruolo')}
          </button>
          {predefiniti.length > 0 && <span className="ruoli__etichetta">{t('Predefiniti di CENSO')}</span>}
          {predefiniti.map((r) => (
            <SchedaRuolo key={r.id} r={r} attiva={scelto === r.id} chiavi={tutteLeChiavi} onApri={() => apri(r)} />
          ))}
          <span className="ruoli__etichetta">{t('Personalizzati')}</span>
          {propri.map((r) => (
            <SchedaRuolo key={r.id} r={r} attiva={scelto === r.id} chiavi={tutteLeChiavi} onApri={() => apri(r)} />
          ))}
          {ruoli.data && propri.length === 0 && (
            <p className="field__hint">{t('Nessun ruolo personalizzato: duplica un ruolo predefinito e ritoccalo, o creane uno nuovo.')}</p>
          )}
        </aside>

        {/* --- La matrice --- */}
        {scelto === null ? (
          <div className="ruoli__vuoto">
            <EmptyState
              title={t('Seleziona un ruolo o creane uno personalizzato')}
              hint={t('I ruoli predefiniti costituiscono la base per configurare autorizzazioni specifiche.')}
            />
          </div>
        ) : (
          <section className="ruoli__editor">
            {bloccato && (
              <p className="ruoli__predefinito">
                <span className="chip">{t('Predefinito')}</span>{' '}
                {t('Ruolo predefinito di CENSO: i permessi si aggiornano da soli con le nuove funzioni, ognuna col livello della sua area. Si assegna così com’è; per cambiarlo, duplicalo.')}
              </p>
            )}
            <div className="row">
              <label className="field">
                <span>{t('Nome del ruolo')}</span>
                <input
                  value={bozza.name}
                  disabled={bloccato}
                  maxLength={80}
                  onChange={(e) => { setBozza({ ...bozza, name: e.target.value }); setSalvato(false) }}
                  placeholder={t('Help desk')}
                />
              </label>
              <label className="field">
                <span>{t('Descrizione')}</span>
                <input
                  value={bozza.description}
                  disabled={bloccato}
                  maxLength={500}
                  onChange={(e) => { setBozza({ ...bozza, description: e.target.value }); setSalvato(false) }}
                  placeholder={t('A chi serve e perché')}
                />
              </label>
            </div>

            <div className="ruoli__colori" role="radiogroup" aria-label={t('Colore')}>
              {COLORI.map((c) => (
                <button
                  type="button"
                  key={c}
                  role="radio"
                  aria-checked={bozza.color === c}
                  aria-label={c}
                  disabled={bloccato}
                  className={`ruoli__colore${bozza.color === c ? ' ruoli__colore--scelto' : ''}`}
                  style={{ background: c }}
                  onClick={() => { setBozza({ ...bozza, color: c }); setSalvato(false) }}
                />
              ))}
            </div>

            <h4 className="ruoli__titoletto">{t('Ruolo di partenza')}</h4>
            <div className="ruoli__partenze" role="radiogroup">
              {ruoliDiPartenza().map((b) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={bozza.base_role === b.value}
                  key={b.value}
                  disabled={bloccato}
                  className={`ruoli__partenza${bozza.base_role === b.value ? ' ruoli__partenza--scelta' : ''}`}
                  onClick={() => cambiaPartenza(b.value)}
                >
                  <strong>{b.label}</strong>
                  <small>{b.hint}</small>
                </button>
              ))}
            </div>

            <div className="ruoli__barra">
              <h4 className="ruoli__titoletto">{t('Permessi per modulo')}</h4>
              {!bloccato && <span className="ruoli__rapidi">
                {LIVELLI.map((l) => (
                  <button
                    type="button"
                    key={l}
                    className="link"
                    disabled={efficace(bozza, l) !== l}
                    onClick={() => imposta([...tutteLeChiavi, '*'], l)}
                  >
                    {t('Tutto: {livello}', { livello: etichettaLivello(l) })}
                  </button>
                ))}
              </span>}
            </div>

            <div className="permessi">
              {catalogo.data.groups.map((g) => {
                const chiavi = g.modules.map((m) => m.key)
                const livelli = new Set(chiavi.map((k) => efficace(bozza, livello(bozza, k))))
                const comune = livelli.size === 1 ? [...livelli][0] : null
                const aperto = !chiusi.includes(g.key)
                return (
                  <div className="permessi__gruppo" key={g.key}>
                    <div className="permessi__riga permessi__riga--gruppo">
                      <button
                        type="button"
                        className="permessi__apri"
                        aria-expanded={aperto}
                        onClick={() =>
                          setChiusi((c) => (c.includes(g.key) ? c.filter((x) => x !== g.key) : [...c, g.key]))
                        }
                      >
                        <span className={`permessi__freccia${aperto ? ' permessi__freccia--aperta' : ''}`} aria-hidden="true">
                          ▸
                        </span>
                        {t(g.label)}
                        {comune === null && <span className="permessi__misto">{t('misto')}</span>}
                      </button>
                      <Selettore
                        valore={comune}
                        base={bozza.base_role}
                        bloccato={bloccato}
                        onChange={(l) => imposta(chiavi, l)}
                        etichetta={t(g.label)}
                      />
                    </div>
                    {aperto &&
                      g.modules.map((m) => (
                        <div className={`permessi__riga${m.parent ? ' permessi__riga--sotto' : ''}`} key={m.key}>
                          <span className="permessi__modulo">
                            {m.parent ? `↳ ${t(m.label)}` : t(m.label)}
                            {m.parent === 'credentials' && m.key === 'cred_rete' && (
                              <small>{t('Le credenziali, categoria per categoria: chi vede quali.')}</small>
                            )}
                          </span>
                          <Selettore
                            valore={efficace(bozza, livello(bozza, m.key))}
                            base={bozza.base_role}
                            bloccato={bloccato}
                            onChange={(l) => imposta([m.key], l)}
                            etichetta={t(m.label)}
                          />
                        </div>
                      ))}
                  </div>
                )
              })}
              <div className="permessi__riga permessi__riga--futuri">
                <span className="permessi__modulo">
                  {t('Moduli futuri')}
                  <small>{t('Il livello delle sezioni che arriveranno con i prossimi aggiornamenti.')}</small>
                </span>
                <Selettore
                  valore={efficace(bozza, bozza.permissions['*'] ?? 'read')}
                  base={bozza.base_role}
                  bloccato={bloccato}
                  onChange={(l) => imposta(['*'], l)}
                  etichetta={t('Moduli futuri')}
                />
              </div>
            </div>

            <div className="ruoli__legenda">
              {LIVELLI.map((l) => (
                <span key={l}>
                  <span className={`livello-pastiglia livello-pastiglia--${l}`}>{ICONA[l]}</span>{' '}
                  <strong>{etichettaLivello(l)}</strong> — {spiegazioneLivello(l)}
                </span>
              ))}
            </div>

            <div className="ruoli__azioni">
              {bloccato ? (
                <button type="button" onClick={duplica}>
                  {t('Duplica per personalizzarlo')}
                </button>
              ) : (
                <button type="button" onClick={salva} disabled={salvo || bozza.name.trim() === ''}>
                  {salvo ? t('Salvataggio…') : scelto === 'nuovo' ? t('Crea ruolo') : t('Salva modifiche')}
                </button>
              )}
              {corrente && !bloccato && (
                <>
                  <button type="button" className="secondary" onClick={duplica}>
                    {t('Duplica')}
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={elimina}
                    disabled={corrente.users > 0}
                    title={corrente.users > 0 ? t('Assegnato a {n} utenti: assegna loro un altro ruolo prima.', { n: corrente.users }) : undefined}
                  >
                    {t('Elimina')}
                  </button>
                </>
              )}
              {salvato && <span className="ruoli__salvato">✓ {t('Salvato')}</span>}
            </div>
            <p className="field__hint">
              {t('Le modifiche ai permessi valgono entro pochi secondi per chi ha il ruolo; un cambio del ruolo di partenza vale dal prossimo accesso.')}
            </p>
          </section>
        )}

        {/* --- L'anteprima del menu --- */}
        {scelto !== null && (
          <aside className="ruoli__anteprima" aria-label={t('Anteprima del menu')}>
            <div className="anteprima__testa">
              <span className="ruolo-scheda__colore" style={{ background: bozza.color }} />
              <strong>{bozza.name || t('Nuovo ruolo')}</strong>
            </div>
            <span className="ruoli__etichetta">{t('Anteprima del menu')}</span>
            <Anteprima catalogo={catalogo.data} bozza={bozza} />
          </aside>
        )}
      </div>
    </Card>
  )
}

/** A role in the list on the left. */
function SchedaRuolo({ r, attiva, chiavi, onApri }: {
  r: RuoloPersonalizzato
  attiva: boolean
  chiavi: string[]
  onApri: () => void
}) {
  return (
    <button type="button" className={`ruolo-scheda${attiva ? ' ruolo-scheda--attiva' : ''}`} onClick={onApri}
      title={r.preset ? t(r.description) : r.description || undefined}>
      <span className="ruolo-scheda__testa">
        <span className="ruolo-scheda__colore" style={{ background: r.color }} />
        <strong>{nomeDelRuolo(r)}</strong>
      </span>
      <span className="ruolo-scheda__meta">
        {ruoliDiPartenza().find((b) => b.value === r.base_role)?.label} · {t('{n} utenti', { n: r.users })}
      </span>
      <Distribuzione permessi={r.permissions} chiavi={chiavi} base={r.base_role} />
    </button>
  )
}

/** I quattro livelli come controllo segmentato. `valore` null = gruppo misto. */
function Selettore({
  valore,
  base,
  bloccato = false,
  onChange,
  etichetta,
}: {
  valore: LivelloPermesso | null
  base: string
  bloccato?: boolean
  onChange: (l: LivelloPermesso) => void
  etichetta: string
}) {
  return (
    <span className="selettore-livello" role="radiogroup" aria-label={etichetta}>
      {LIVELLI.map((l) => {
        const vietato = base === 'read_only' && l === 'write'
        return (
          <button
            type="button"
            key={l}
            role="radio"
            aria-checked={valore === l}
            disabled={vietato || (bloccato && valore !== l)}
            title={vietato ? t('Il ruolo di partenza è in sola lettura') : spiegazioneLivello(l)}
            className={`selettore-livello__voce selettore-livello__voce--${l}${valore === l ? ' selettore-livello__voce--scelta' : ''}`}
            onClick={() => onChange(l)}
          >
            <span aria-hidden="true">{ICONA[l]}</span>
            <span className="selettore-livello__testo">{etichettaLivello(l)}</span>
          </button>
        )
      })}
    </span>
  )
}

/** Il menu come lo vedrà chi ha il ruolo. */
function Anteprima({ catalogo, bozza }: { catalogo: CatalogoRuoli; bozza: RuoloPersonalizzatoIn }) {
  const gruppi = catalogo.groups
    .map((g) => ({
      ...g,
      // I sottomoduli (le categorie delle credenziali) non sono voci di menu.
      voci: g.modules
        .filter((m) => !m.parent)
        .map((m) => ({ ...m, livello: efficace(bozza, livello(bozza, m.key)) }))
        .filter((m) => m.livello !== 'hidden'),
    }))
    .filter((g) => g.voci.length > 0)
  if (gruppi.length === 0) {
    return <p className="field__hint">{t('Con questi permessi il menu è vuoto: chi ha il ruolo non vedrà nulla.')}</p>
  }
  return (
    <div className="anteprima">
      {gruppi.map((g) => (
        <div key={g.key} className="anteprima__gruppo">
          <div className="anteprima__sezione">{t(g.label)}</div>
          {g.voci.map((m) => (
            <div key={m.key} className={`anteprima__voce anteprima__voce--${m.livello}`}>
              <span>{t(m.label)}</span>
              <span className="anteprima__icona" aria-label={etichettaLivello(m.livello)}>
                {m.livello === 'write' ? '' : ICONA[m.livello]}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/** Una barra con la proporzione dei livelli: il ruolo riassunto a colpo d'occhio. */
function Distribuzione({
  permessi,
  chiavi,
  base,
}: {
  permessi: Record<string, LivelloPermesso>
  chiavi: string[]
  base: string
}) {
  const conta: Record<LivelloPermesso, number> = { hidden: 0, disabled: 0, read: 0, write: 0 }
  for (const k of chiavi) {
    let l = permessi[k] ?? permessi['*'] ?? 'read'
    if (base === 'read_only' && l === 'write') l = 'read'
    conta[l] += 1
  }
  const totale = chiavi.length || 1
  return (
    <span
      className="distribuzione"
      title={LIVELLI.map((l) => `${etichettaLivello(l)}: ${conta[l]}`).join(' · ')}
    >
      {[...LIVELLI].reverse().map((l) =>
        conta[l] > 0 ? (
          <span
            key={l}
            className={`distribuzione__parte distribuzione__parte--${l}`}
            style={{ width: `${(conta[l] / totale) * 100}%` }}
          />
        ) : null,
      )}
    </span>
  )
}
